import { useLocation } from 'react-router'
import { analytics, type SCREENS } from 'services/Analytics/Analytics'
import { _EVENT_NAMES as EVENT_NAMES } from 'services/Analytics/AnalyticsEvents'
import { generateProductCountEventProperties } from 'services/Analytics/productsAnalytics'
import { ServerSideAnalyticsPlatform } from 'services/Analytics/ServerSideAnalytics'
import useScrollToBottom from 'services/Analytics/useScrollToBottom'
import { useProductType } from 'services/ProductTypesManager'
import {
  MEASUREMENT_LABELS,
  measureTime,
  shouldMeasure,
} from 'services/uptimeMeasure'
import useOnMount from 'hooks/useOnMount'
import { isClient } from 'utils/runtimeUtils'

type Props = {
  screenName: (typeof SCREENS)[keyof typeof SCREENS]
  eventProperties?: Record<string, unknown>
}

type ExtraProps = Record<string, unknown>

export default function useHomePageAnalytics({
  screenName,
  eventProperties = {},
}: Props) {
  const { productType } = useProductType()
  const location = useLocation()

  const productCountProperties = isClient()
    ? generateProductCountEventProperties()
    : {}
  const analyticsProps = {
    'set type': productType,
    Type: screenName,
    Source: location?.state?.source || 'Direct',
    ...productCountProperties,
    ...eventProperties,
  }

  useOnMount(() => {
    analytics.trackScreenView(screenName, eventProperties)
    analytics.track(EVENT_NAMES.HOMEPAGE_RENDERED, {
      ...analyticsProps,
      Time: shouldMeasure()
        ? measureTime(MEASUREMENT_LABELS.HOMEPAGE)
        : undefined,
    })
  })

  useScrollToBottom({ screenName, analyticsProps })

  const trackMainCta = (extraProps: ExtraProps = {}) => {
    analytics.trackFirstInteraction()
    analytics.track(
      EVENT_NAMES.HOMEPAGE_MAIN_CTA_TAPPED,
      { ...analyticsProps, ...extraProps },
      {
        // @ts-ignore - serverSideAnalyticsPlatforms is defined as never[] in the Analytics.js
        serverSideAnalyticsPlatforms: [ServerSideAnalyticsPlatform.Tiktok],
      }
    )
  }

  const trackSecondaryCta = (extraProps: ExtraProps = {}) => {
    analytics.trackFirstInteraction()
    analytics.track(EVENT_NAMES.HOMEPAGE_CTA_TAPPED, {
      ...analyticsProps,
      ...extraProps,
    })
  }

  return { analyticsProps, trackMainCta, trackSecondaryCta }
}
